/**
 * Shared builders + constants for the CRM `SimpleTableConfig<T>` literals emitted
 * by the per-entity `*Config.ts` files (contracts, social posts, email campaigns,
 * recurring transactions, …).
 *
 * DESIGN — structural identity is a hard requirement. Each field builder sets the
 * discriminant `type` (and, for foreign keys, the fixed `isForeignKey`/`foreignKey`
 * lead) and then SPREADS the caller's option object. Object spread preserves
 * insertion order, so as long as a call lists the remaining keys in the same order
 * as the original inline literal, the emitted field object is byte-for-byte
 * identical — same keys, same values, same order. Builders never inject default
 * flags, precisely because a default would land in a fixed position and could
 * reorder keys relative to the hand-written original.
 *
 * If a field's inline key order cannot be reproduced this way, leave it inline.
 */

import type { SimpleFieldConfig } from "@/lib/types/table/field-config-types";
import type { FieldType } from "@/lib/types/table/field-config-subtypes";
import type {
  BulkAction,
  TableFeatures,
} from "@/lib/types/table/table-config-types";
import type { BaseEntity } from "@/lib/types/table/base-types";

/** Field options minus the discriminant the builder supplies. */
type FieldOptions = Omit<SimpleFieldConfig, "type">;

/** `type: "text"` + the caller's options (order preserved). */
export const textField = (opts: FieldOptions): SimpleFieldConfig => ({
  type: "text",
  ...opts,
});

/** `type: "select"` + the caller's options (order preserved). */
export const selectField = (opts: FieldOptions): SimpleFieldConfig => ({
  type: "select",
  ...opts,
});

/** `type: "number"` + the caller's options (order preserved). */
export const numberField = (opts: FieldOptions): SimpleFieldConfig => ({
  type: "number",
  ...opts,
});

/** `type: "date"` + the caller's options (order preserved). */
export const dateField = (opts: FieldOptions): SimpleFieldConfig => ({
  type: "date",
  ...opts,
});

/** `type: "datetime"` + the caller's options (order preserved). */
export const datetimeField = (opts: FieldOptions): SimpleFieldConfig => ({
  type: "datetime",
  ...opts,
});

/**
 * Foreign-key field. Emits `{ type, label, isForeignKey: true, foreignKey, ...rest }`
 * — matching the inline fk fields' order (type → label → isForeignKey → foreignKey
 * → everything else). `type` is explicit because fk columns render via several
 * field types (`company-select`, `client-select`, `user`, `text`, `select`).
 */
export const fkField = (
  type: FieldType,
  opts: Omit<SimpleFieldConfig, "type" | "isForeignKey">
): SimpleFieldConfig => {
  const { label, foreignKey, ...rest } = opts;
  return { type, label, isForeignKey: true, foreignKey, ...rest };
};

/**
 * The table feature-flag set shared by the CRM configs. Keys are ordered exactly
 * as the inline `features` blocks were. Configs that differ spread-and-override:
 *   contracts  → `{ ...STANDARD_TABLE_FEATURES, bulkActions: false }`
 *   recurring  → `{ ...STANDARD_TABLE_FEATURES, infiniteScroll: true, calendarView: true }`
 * (overriding an existing key keeps its position; `calendarView` appends last —
 * both identical to the original literals).
 */
export const STANDARD_TABLE_FEATURES = {
  search: true,
  filters: true,
  export: true,
  bulkActions: true,
  columnResizing: true,
  columnVisibility: true,
  savedViews: true,
  infiniteScroll: false,
  multiColumnSort: true,
} as const satisfies TableFeatures;

/**
 * The standard CRM list-view bulk actions (cancel / delete / export). A factory
 * so each config gets a correctly-typed `BulkAction<Entity>[]` with no cast; the
 * emitted array is identical to the previous inline literals. Ids match the
 * handlers in `genericBulkActions.entityConfigs.ts`.
 */
export const standardCrmBulkActions = <
  T extends BaseEntity = BaseEntity,
>(): BulkAction<T>[] => [
  { id: "bulk_cancel", label: "Cancel", icon: "x" },
  { id: "bulk_delete", label: "Delete", icon: "trash", variant: "destructive" },
  { id: "bulk_export", label: "Export", icon: "download" },
];
